import React, { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Briefcase, UserRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/sonner";
import { useAuth, resolvePostAuthPath } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";

const GoogleButton = ({ role }) => {
    const startGoogle = () => {
        localStorage.setItem("simonara_auth_role", role);
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };
    return (
        <button type="button" onClick={startGoogle} data-testid="auth-google-btn"
            className="w-full inline-flex items-center justify-center gap-3 rounded-full border border-border bg-card py-3 font-semibold text-foreground hover:bg-muted transition-colors">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.2 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.2 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41.4 35.4 44 30.2 44 24c0-1.3-.1-2.6-.4-3.9z"/></svg>
            Continue with Google
        </button>
    );
};

export default function AuthPage({ mode = "signup" }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [params] = useSearchParams();
    const { login, register } = useAuth();

    const isLogin = mode === "login";
    const [role, setRole] = useState(params.get("role") === "employer" ? "employer" : "professional");
    const [loading, setLoading] = useState(false);
    // Track password + name + email so the strength meter can penalize passwords
    // that reuse a user's identity (zxcvbn's ``user_inputs`` heuristic).
    const [pwLive, setPwLive] = useState("");
    const [nameLive, setNameLive] = useState("");
    const [emailLive, setEmailLive] = useState(params.get("email") || "");
    // Legal consent checkboxes — required at signup.
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
    const [acceptedAiAdvisory, setAcceptedAiAdvisory] = useState(false);
    const [acceptedRoleCert, setAcceptedRoleCert] = useState(false);
    const [regionBlock, setRegionBlock] = useState(null);
    const [waitlistEmail, setWaitlistEmail] = useState("");
    const [waitlistDone, setWaitlistDone] = useState(false);
    // Talent age gate — DOB is required for professional signup (see
    // /app/memory/PENDING_LEGAL_REVIEW.md and config_age.py). Employer
    // signup does NOT ask for or send this field.
    const [dob, setDob] = useState("");
    const hasPendingIntake = !!localStorage.getItem("simonara_pending_intake");

    const roleCertText = role === "employer"
        ? "I certify that I represent a legitimate employer or hiring organization, I agree to use candidate information solely for lawful hiring purposes, and I understand AI recommendations are decision-support tools and should not be the sole basis for employment decisions."
        : "I certify that all information I submit is true and accurate; I understand Simonara does not guarantee employment or interviews; I consent to my Talent Passport being processed for AI matching and career recommendations.";

    const canSubmit = isLogin
        ? true
        : (acceptedTerms && acceptedPrivacy && acceptedAiAdvisory && acceptedRoleCert
           && (role !== "professional" || !!dob));

    const submit = async (e) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        try {
            setLoading(true);
            let user;
            if (isLogin) {
                user = await login(fd.get("email"), fd.get("password"));
            } else {
                if (!canSubmit) {
                    toast.error("Please review and accept all required agreements.");
                    setLoading(false);
                    return;
                }
                user = await register({
                    email: fd.get("email"),
                    password: fd.get("password"),
                    name: fd.get("name"),
                    role,
                    company_name: role === "employer" ? (fd.get("company_name") || "") : "",
                    employer_website: role === "employer" ? (fd.get("employer_website") || "") : "",
                    // Talent-only DOB. Employer signup omits the field entirely.
                    ...(role === "professional" ? { dob } : {}),
                    accepted_terms: acceptedTerms,
                    accepted_privacy: acceptedPrivacy,
                    accepted_ai_advisory: acceptedAiAdvisory,
                    accepted_role_cert: acceptedRoleCert,
                    // Referral tracking: forward ?ref= into the signup so the referrer earns credit.
                    referral_code: params.get("ref") || "",
                    // Simonara Recruit™ invite loop: /signup?invite=<token> lands here
                    // from an outreach email — forwarding closes the attribution loop.
                    invite_token: params.get("invite") || "",
                });
            }
            toast.success(isLogin ? `Welcome, ${user.name || user.email}.` : "Account created — welcome to Simonara.");
            // Respect an explicit ?next= redirect target from the query string
            // (e.g. Passport-First Apply routes here with ?next=/jobs/:slug/apply
            // so the user lands on the apply screen the moment their account
            // exists). Only accept SAME-ORIGIN paths — never external URLs —
            // to prevent open-redirect abuse. Falls back to the role-aware
            // resolvePostAuthPath for the normal signup / login flow.
            const rawNext = params.get("next") || "";
            const safeNext =
                rawNext && rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "";
            const path = safeNext || (await resolvePostAuthPath(user));
            navigate(path, { state: { user } });
        } catch (err) {
            // iter-136: non-US talent signup returns a structured region_blocked
            // 403. Surface the international waitlist prompt inline so we don't
            // just show a generic toast.
            const detail = err?.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.code === "region_blocked") {
                setRegionBlock({
                    message: detail.message || "Simonara is currently US-only.",
                    country: detail.country || "",
                });
                setLoading(false);
                return;
            }
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[85vh] bg-background py-16">
            <div className="container-page max-w-md">
                {hasPendingIntake && !isLogin && (
                    <div data-testid="auth-pending-intake-banner" className="mb-5 rounded-2xl border border-emerald-500/30 bg-accent p-4 text-sm text-accent-foreground dark:text-emerald-300">
                        Your job is ready. Create your employer profile and Simonara AI will start analyzing it immediately.
                    </div>
                )}
                {!isLogin && params.get("ref") && (
                    <div data-testid="auth-referral-banner" className="mb-5 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-900 dark:text-amber-200">
                        🎁 You&apos;re joining via a referral. Publish your first role and your friend earns a free 7-day Featured Job promotion.
                    </div>
                )}
                {regionBlock && (
                    <div className="mb-5 rounded-2xl border border-sky-500/30 bg-sky-500/10 p-5" data-testid="auth-region-blocked">
                        <h2 className="font-heading font-semibold text-lg">We&apos;re US-only for now</h2>
                        <p className="mt-1 text-sm">{regionBlock.message}</p>
                        {waitlistDone ? (
                            <p className="mt-3 text-sm text-emerald-700 font-semibold" data-testid="auth-waitlist-done">
                                Thanks — you&apos;re on the list. We&apos;ll email you when we expand.
                            </p>
                        ) : (
                            <form onSubmit={async (e) => {
                                e.preventDefault();
                                try {
                                    await (await import("@/lib/api")).default.post("/public/waitlist/international", {
                                        email: waitlistEmail,
                                        country: regionBlock.country,
                                        context: "talent_signup_blocked",
                                    });
                                    setWaitlistDone(true);
                                } catch { toast.error("Try again shortly."); }
                            }} className="mt-3 flex gap-2">
                                <input type="email" required
                                    value={waitlistEmail}
                                    onChange={(e) => setWaitlistEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm"
                                    data-testid="auth-waitlist-email" />
                                <button type="submit"
                                    className="rounded-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2"
                                    data-testid="auth-waitlist-submit">
                                    Join waitlist
                                </button>
                            </form>
                        )}
                    </div>
                )}
                <div className="card-soft p-8">
                    <h1 className="font-heading font-semibold text-2xl text-foreground">
                        {isLogin ? "Log in to Simonara" : "Create your Simonara profile"}
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {isLogin ? "Access your dashboard and AI tools." : "One profile powers everything — AI matching, Talent Passports, and hiring briefs."}
                    </p>

                    {!isLogin && (
                        <div className="mt-6 grid grid-cols-2 gap-2">
                            {[
                                { key: "professional", label: "Talent", icon: UserRound },
                                { key: "employer", label: "Employer", icon: Briefcase },
                            ].map((r) => (
                                <button key={r.key} type="button" onClick={() => setRole(r.key)}
                                    data-testid={`auth-role-${r.key}`}
                                    className={`flex items-center justify-center gap-2 rounded-xl border py-3 text-sm font-semibold transition-colors ${
                                        role === r.key ? "border-emerald-500 bg-accent text-accent-foreground dark:text-emerald-300" : "border-border text-muted-foreground hover:bg-muted"
                                    }`}>
                                    <r.icon size={15} /> {r.label}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="mt-6">
                        <GoogleButton role={role} />
                    </div>
                    <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="h-px flex-1 bg-border" /> or with email <span className="h-px flex-1 bg-border" />
                    </div>

                    <form onSubmit={submit} className="space-y-4">
                        {!isLogin && (
                            <div>
                                <Label htmlFor="auth-name">Full name</Label>
                                <Input id="auth-name" name="name" required onChange={(e) => setNameLive(e.target.value)} data-testid="auth-name" className="mt-2" />
                            </div>
                        )}
                        {!isLogin && role === "professional" && (
                            <div>
                                <Label htmlFor="auth-dob">Date of birth</Label>
                                <Input
                                    id="auth-dob"
                                    name="dob"
                                    type="date"
                                    required
                                    value={dob}
                                    onChange={(e) => setDob(e.target.value)}
                                    data-testid="auth-dob"
                                    className="mt-2"
                                    autoComplete="bday"
                                    max={new Date().toISOString().slice(0, 10)}
                                />
                                <p className="mt-1 text-[11px] text-muted-foreground">
                                    You must be 18 or older to create a Talent Passport. Your DOB is stored encrypted and is never shared with employers.
                                </p>
                                <details className="mt-2 text-[11px]" data-testid="auth-dob-faq">
                                    <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                                        Why do we ask for this?
                                    </summary>
                                    <div className="mt-2 space-y-2 rounded-lg bg-muted/40 p-3 text-muted-foreground leading-relaxed">
                                        <p>
                                            <strong className="text-foreground">The 18+ rule.</strong> Simonara Talent Solutions is for adults. We collect date of birth once, at signup, to verify you meet the platform's minimum age.
                                        </p>
                                        <p>
                                            <strong className="text-foreground">What we do with it.</strong> Your DOB is encrypted at rest and used only for the age check plus the FCRA-compliant background-check flow. It is never shown to employers or recruiters, never appears on your Talent Passport, and cannot be searched or filtered by any employer.
                                        </p>
                                        <p>
                                            <strong className="text-foreground">Can I change it later?</strong> Once set, DOB is locked. If you entered it wrong, contact <a href="mailto:support@simonaratalent.us" className="underline hover:text-foreground">support@simonaratalent.us</a> and we'll help correct it after a quick identity verification.
                                        </p>
                                        <p>
                                            <strong className="text-foreground">Can I delete it?</strong> Yes — DOB is covered by the <Link to="/data-request" className="underline hover:text-foreground">Data Request</Link> flow like every other piece of your data, and auto-purges under the same 2-year retention rule.
                                        </p>
                                    </div>
                                </details>
                            </div>
                        )}
                        {!isLogin && role === "employer" && (
                            <>
                                <div>
                                    <Label htmlFor="auth-company">Company name</Label>
                                    <Input id="auth-company" name="company_name" required data-testid="auth-company" className="mt-2" />
                                </div>
                                <div>
                                    <Label htmlFor="auth-emp-site">Company website</Label>
                                    <Input
                                        id="auth-emp-site"
                                        name="employer_website"
                                        type="url"
                                        placeholder="https://acme.com"
                                        data-testid="auth-employer-website"
                                        className="mt-2"
                                    />
                                    <p className="mt-1 text-[11px] text-muted-foreground">
                                        Optional. Used to auto-verify teammates you invite from the same email domain.
                                    </p>
                                </div>
                            </>
                        )}
                        <div>
                            <Label htmlFor="auth-email">Email</Label>
                            <Input id="auth-email" name="email" type="email" required value={emailLive} onChange={(e) => setEmailLive(e.target.value)} data-testid="auth-email" className="mt-2" />
                        </div>
                        <div>
                            <Label htmlFor="auth-password">Password {!isLogin && <span className="text-muted-foreground font-normal">(min 8 characters)</span>}</Label>
                            <Input
                                id="auth-password"
                                name="password"
                                type="password"
                                required
                                minLength={isLogin ? undefined : 8}
                                value={pwLive}
                                onChange={(e) => setPwLive(e.target.value)}
                                data-testid="auth-password"
                                className="mt-2"
                            />
                            {!isLogin && (
                                <PasswordStrengthMeter
                                    password={pwLive}
                                    userInputs={[emailLive, nameLive, "simonara"]}
                                    testId="auth-password-strength"
                                />
                            )}
                            {isLogin && (
                                <div className="mt-1 text-right">
                                    <Link
                                        to="/forgot-password"
                                        data-testid="auth-forgot-password-link"
                                        className="text-xs font-medium text-muted-foreground hover:text-emerald-600 hover:underline"
                                    >
                                        Forgot password?
                                    </Link>
                                </div>
                            )}
                        </div>

                        {!isLogin && (
                            <div className="mt-2 space-y-2 rounded-xl border border-dashed border-border p-3" data-testid="auth-legal-block">
                                <label className="flex items-start gap-2 text-xs text-foreground">
                                    <Checkbox
                                        checked={acceptedTerms}
                                        onCheckedChange={(v) => setAcceptedTerms(!!v)}
                                        className="mt-0.5"
                                        data-testid="auth-check-terms"
                                    />
                                    <span>
                                        I agree to the <Link to="/terms" target="_blank" className="underline hover:text-emerald-600">Terms of Service</Link>.
                                    </span>
                                </label>
                                <label className="flex items-start gap-2 text-xs text-foreground">
                                    <Checkbox
                                        checked={acceptedPrivacy}
                                        onCheckedChange={(v) => setAcceptedPrivacy(!!v)}
                                        className="mt-0.5"
                                        data-testid="auth-check-privacy"
                                    />
                                    <span>
                                        I have read and acknowledge the <Link to="/privacy" target="_blank" className="underline hover:text-emerald-600">Privacy Policy</Link>.
                                    </span>
                                </label>
                                <label className="flex items-start gap-2 text-xs text-foreground">
                                    <Checkbox
                                        checked={acceptedAiAdvisory}
                                        onCheckedChange={(v) => setAcceptedAiAdvisory(!!v)}
                                        className="mt-0.5"
                                        data-testid="auth-check-ai"
                                    />
                                    <span>I understand AI recommendations are advisory only.</span>
                                </label>
                                <label className="flex items-start gap-2 text-xs text-foreground">
                                    <Checkbox
                                        checked={acceptedRoleCert}
                                        onCheckedChange={(v) => setAcceptedRoleCert(!!v)}
                                        className="mt-0.5"
                                        data-testid="auth-check-rolecert"
                                    />
                                    <span>{roleCertText}</span>
                                </label>
                            </div>
                        )}

                        <button type="submit" disabled={loading || !canSubmit} data-testid="auth-submit" className="btn-green w-full disabled:opacity-60">
                            {loading ? <><Loader2 size={18} className="mr-2 animate-spin" /> {isLogin ? "Logging in…" : "Creating…"}</> : (isLogin ? "Log in" : "Create account")}
                        </button>
                    </form>

                    <p className="mt-6 text-center text-sm text-muted-foreground">
                        {isLogin ? (
                            <>New to Simonara? <Link to={`/signup${location.search}`} data-testid="auth-switch-signup" className="font-semibold text-brand-green dark:text-emerald-400 hover:underline">Create a profile</Link></>
                        ) : (
                            <>Already have an account? <Link to={`/login${location.search}`} data-testid="auth-switch-login" className="font-semibold text-brand-green dark:text-emerald-400 hover:underline">Log in</Link></>
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
}
